/* eslint-disable camelcase */
import axios, { AxiosResponse } from 'axios';
import { getIsDemoExperience } from 'services/globalConfig.services';
import { RatingDTO } from '../interfaces/dto/RatingDTO.interface';
import ENDPOINTS from '../constants/endpoints.constants';
import APIError from '../exceptions/APIError';

const axiosInstance = axios.create({
  baseURL: `${ENDPOINTS.TRACKING_ANTENNA_HOST}/api/v1/ptp/rating/`,
});

/**
 * Stable for the life of the page. The update's URL is built from the id the
 * create returns, so an id that changed between calls would make submit fail
 * *after* the prospect has typed into the dialog.
 */
const DEMO_RATING_ID = 'demo-rating-1';

/**
 * The rating service unwraps exactly one `data` level before mapping, so the
 * stub mimics an AxiosResponse rather than nesting the body twice. A wrong
 * shape throws inside the mapper and the widget shows its rating error.
 */
const demoResponse = (rating: RatingDTO): AxiosResponse<RatingDTO> =>
  ({ data: rating } as AxiosResponse<RatingDTO>);

export const getRateByShipmentID = async (
  shipmentId: number,
): Promise<AxiosResponse<RatingDTO>> => {
  if (getIsDemoExperience()) {
    // Value 0 means "not rated yet". A non-zero value renders pre-filled stars
    // and the widget short-circuits, so clicking would do nothing — which
    // removes the one interaction the demo exists to show.
    //
    // `tracking_exp_id` and `organization_id` are 0 rather than invented ids:
    // the hook builds its default rate from the widget's own config and the
    // create echoes the real values back, so nothing reads these.
    return demoResponse({
      id: DEMO_RATING_ID,
      shipment_id: shipmentId,
      tracking_exp_id: 0,
      organization_id: 0,
      value: 0,
      categories: [],
    });
  }

  try {
    const url = `${shipmentId}/`;
    const { data } = await axiosInstance.get(url);
    return data;
  } catch (error) {
    if (error.response) {
      throw new APIError(error.response.data?.message, error);
    }
    throw error;
  }
};

export const postRate = async (
  rate: RatingDTO,
): Promise<AxiosResponse<RatingDTO>> => {
  if (getIsDemoExperience()) {
    // Echo what the service sent, and pin the id. The widget renders from the
    // response, not from local state, so a canned body would discard the
    // prospect's own selection in front of them.
    return demoResponse({ ...rate, id: DEMO_RATING_ID });
  }

  try {
    const url = `${rate.shipment_id}/create/`;
    const { data } = await axiosInstance.post(url, {
      ...rate,
    });

    return data;
  } catch (error) {
    if (error.response) {
      throw new APIError(error.response.data?.message, error);
    }
    throw error;
  }
};

export const putRate = async (
  rate: RatingDTO,
): Promise<AxiosResponse<RatingDTO>> => {
  if (getIsDemoExperience()) {
    return demoResponse({ ...rate, id: DEMO_RATING_ID });
  }

  try {
    const { id, shipment_id, ...rest } = rate;
    const url = `${rate.shipment_id}/update/${id}/`;
    const { data } = await axiosInstance.put(url, {
      shipment_id,
      ...rest,
    });

    return data;
  } catch (error) {
    if (error.response) {
      throw new APIError(error.response.data?.message, error);
    }
    throw error;
  }
};
